import { create } from 'zustand';
import type { Session, Subscription, User } from '@supabase/supabase-js';
import supabase from '@/lib/supabase';
import posthog from 'posthog-js';
import { saveUTMAttributionOnRegistration, updateUTMAttributionOnLogin } from '@/lib/utm-attribution';
import { getReferralAttribution, clearReferralAttribution } from '@/lib/referral-attribution';
import report, { ReportEvent } from '@/lib/report';

/**
 * Attempt to claim referral credit if there's a stored referral attribution.
 * Non-blocking — failures are logged but don't affect auth flow.
 */
async function claimReferralIfNeeded(accessToken: string): Promise<{ teacherJoined: boolean; code: string | null }> {
  const attribution = getReferralAttribution();
  console.log('[Referral] claimReferralIfNeeded called, attribution:', attribution);
  if (!attribution) {
    return { teacherJoined: false, code: null };
  }

  try {
    const res = await fetch('/api/referral/claim', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ code: attribution.referral_code }),
    });

    // Always clear attribution after a registration attempt to prevent
    // the same referral code from being applied to subsequent registrations.
    clearReferralAttribution();

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      console.warn('Referral claim failed:', data.error || res.status);
      return { teacherJoined: false, code: attribution.referral_code.trim().toUpperCase() };
    }

    const data = await res.json().catch(() => ({}));
    return {
      teacherJoined: Boolean(data?.teacher_joined),
      code: attribution.referral_code.trim().toUpperCase(),
    };
  } catch (error) {
    clearReferralAttribution();
    console.warn('Failed to claim referral (non-blocking):', error);
    return { teacherJoined: false, code: attribution.referral_code.trim().toUpperCase() };
  }
}
import { Profile } from '@/constant/schema';
import { getDistrictBySchoolName } from '@/lib/schoolMappingService';
import { mergeClassJoinPromptIntoOnboardData } from '@/lib/class-join-prompt';

interface AuthResult {
  error: string | null;
  needsEmailConfirmation?: boolean;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userDistrict: string | null;
  initialized: boolean;
  isLoading: boolean;
  error: string | null;

  initialize: () => Promise<void>;
  clearError: () => void;

  signInWithPassword: (identifier: string, password: string) => Promise<AuthResult>;
  signUp: (email: string, password: string) => Promise<AuthResult>;

  // Legacy compatibility APIs
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, nickname?: string, role?: string, inviteCode?: string) => Promise<void>;
  signOut: (options?: { redirectTo?: string; hardReload?: boolean }) => Promise<void>;
  checkAuth: () => Promise<User | null>;
  resetPassword: (email: string) => Promise<void>;

  // Profile methods
  getProfile: (userId?: string) => Promise<Profile | null>;
  updateProfile: (updates: Partial<Profile>) => Promise<Profile | null>;
}

let authSubscription: Subscription | null = null;

function identifyPostHogUser(user: User, traits: Record<string, unknown> = {}) {
  const analyticsEmail = user.email;

  posthog.identify(user.id, {
    email: analyticsEmail,
    name: user.user_metadata?.nickname || analyticsEmail?.split('@')[0] || user.id,
    provider: user.app_metadata?.provider || 'email',
    created_at: user.created_at,
    last_seen: new Date().toISOString(),
    ...traits,
  });
}

const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  profile: null,
  userDistrict: null,
  initialized: false,
  isLoading: false,
  error: null,

  clearError: () => set({ error: null }),

  initialize: async () => {
    if (get().initialized) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }

      const session = data.session ?? null;
      const user = session?.user ?? null;

      set({
        session,
        user,
        initialized: true,
        isLoading: false,
        error: null,
      });

      if (user) {
        try {
          await get().getProfile(user.id);
        } catch (profileError) {
          console.warn('Failed to load profile during initialize (non-blocking):', profileError);
        }
      } else {
        set({ profile: null, userDistrict: null });
      }

      if (!authSubscription) {
        const { data: subscriptionData } = supabase.auth.onAuthStateChange((_event, nextSession) => {
          const nextUser = nextSession?.user ?? null;

          set({
            session: nextSession ?? null,
            user: nextUser,
            profile: nextUser ? get().profile : null,
            userDistrict: nextUser ? get().userDistrict : null,
          });

          if (nextUser) {
            try {
              identifyPostHogUser(nextUser);
            } catch (posthogError) {
              console.warn('Failed to identify user in PostHog during auth state change (non-blocking):', posthogError);
            }
          } else {
            try {
              posthog.reset();
            } catch (posthogError) {
              console.warn('Failed to reset PostHog during auth state change (non-blocking):', posthogError);
            }
          }
        });
        authSubscription = subscriptionData.subscription;
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      set({
        session: null,
        user: null,
        profile: null,
        userDistrict: null,
        initialized: true,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to initialize auth',
      });
    }
  },

  getProfile: async (userId?: string) => {
    const targetUserId = userId || get().user?.id;
    if (!targetUserId) {
      return null;
    }

    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', targetUserId).single();
      if (error) {
        throw error;
      }

      const district = await getDistrictBySchoolName(data?.onboard_data?.profile?.school?.school_name);

      if (!userId || userId === get().user?.id) {
        set({ profile: data, userDistrict: district });
      }

      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  },

  updateProfile: async (updates: Partial<Profile>) => {
    const userId = get().user?.id;
    if (!userId) {
      throw new Error('No authenticated user');
    }

    const previousProfile = get().profile;

    try {
      const optimisticProfile = previousProfile ? { ...previousProfile, ...updates } : null;
      set({ profile: optimisticProfile });

      const district = await getDistrictBySchoolName(updates?.onboard_data?.profile?.school?.school_name);
      set({ userDistrict: district });

      const { error } = await supabase.from('profiles').update(updates).eq('id', userId);
      if (error) {
        throw error;
      }

      return get().profile;
    } catch (error) {
      console.error('Error updating profile:', error);
      set({ profile: previousProfile });
      throw error;
    }
  },

  signInWithPassword: async (identifier: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const normalizedIdentifier = identifier
        .trim()
        .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '')
        .toLowerCase();

      const response = await fetch('/api/auth/password-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: normalizedIdentifier,
          password,
        }),
      });

      const payload = await response.json().catch(() => ({} as any));
      if (!response.ok) {
        const message = payload?.error || 'Invalid login credentials.';
        set({ isLoading: false, error: message });
        return { error: message };
      }

      if (!payload?.session?.accessToken || !payload?.session?.refreshToken) {
        set({ isLoading: false, error: 'Failed to create login session.' });
        return { error: 'Failed to create login session.' };
      }

      const { data: sessionData, error: setSessionError } = await supabase.auth.setSession({
        access_token: payload.session.accessToken,
        refresh_token: payload.session.refreshToken,
      });

      if (setSessionError || !sessionData.session || !sessionData.user) {
        const message = setSessionError?.message || 'Failed to initialize login session.';
        set({ isLoading: false, error: message });
        return { error: message };
      }

      set({
        session: sessionData.session,
        user: sessionData.user,
        isLoading: false,
        error: null,
        initialized: true,
      });

      if (sessionData.user) {
        try {
          await get().getProfile(sessionData.user.id);
        } catch (profileError) {
          console.warn('Failed to load profile after sign-in (non-blocking):', profileError);
        }

        try {
          identifyPostHogUser(sessionData.user, {
            last_login: new Date().toISOString(),
          });
        } catch (posthogError) {
          console.warn('Failed to identify user in PostHog (non-blocking):', posthogError);
        }

        try {
          await updateUTMAttributionOnLogin(
            sessionData.user.id,
            sessionData.user.email || normalizedIdentifier
          );
        } catch (utmError) {
          console.warn('Failed to update UTM attribution on login:', utmError);
        }

        // Claim referral credit on login (covers email-confirmed signups)
        if (sessionData.session?.access_token) {
          await claimReferralIfNeeded(sessionData.session.access_token);
        }
      }

      return { error: null };
    } catch (error) {
      console.error('Error signing in with password:', error);
      const message = error instanceof Error ? error.message : 'Failed to sign in';
      set({ isLoading: false, error: message });
      return { error: message };
    }
  },

  signUp: async (email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      const needsEmailConfirmation = !data.session && !!data.user;

      if (error) {
        set({ isLoading: false, error: error.message });
        return { error: error.message, needsEmailConfirmation };
      }

      set({
        session: data.session ?? null,
        user: data.user ?? null,
        isLoading: false,
        error: null,
        initialized: true,
      });

      if (data.user && data.session) {
        try {
          await get().getProfile(data.user.id);
        } catch (profileError) {
          console.warn('Failed to load profile after sign-up (non-blocking):', profileError);
        }
      }

      if (!error && data.user) {
        try {
          identifyPostHogUser(data.user, {
            created_at: data.user.created_at,
          });
          posthog.capture('user_signed_up', {
            needs_email_confirmation: needsEmailConfirmation,
          });
        } catch (posthogError) {
          console.warn('Failed to capture signup analytics (non-blocking):', posthogError);
        }

        // Google Ads conversion tracking
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'sign_up', { method: 'email' });
        }

        try {
          await saveUTMAttributionOnRegistration(data.user.id, email);
        } catch (utmError) {
          console.warn('Failed to save UTM attribution on registration:', utmError);
        }

        // Reddit Pixel signup conversion (with conversionId for deduplication)
        if (typeof window !== 'undefined' && (window as any).rdt) {
          (window as any).rdt('track', 'SignUp', { conversionId: data.user.id });
        }
        // Reddit Conversions API (server-side, non-blocking)
        fetch('/napi/reddit-capi', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'SignUp',
            conversionId: data.user.id,
            screenWidth: typeof window !== 'undefined' ? window.screen.width : undefined,
            screenHeight: typeof window !== 'undefined' ? window.screen.height : undefined,
          }),
        }).catch(() => {});

        // Claim referral credit if session exists (no email confirmation needed)
        if (data.session?.access_token) {
          await claimReferralIfNeeded(data.session.access_token);
        }
      }

      return { error: null, needsEmailConfirmation };
    } catch (error) {
      console.error('Error signing up:', error);
      const message = error instanceof Error ? error.message : 'Failed to sign up';
      set({ isLoading: false, error: message });
      return { error: message };
    }
  },

  // Legacy compatibility wrappers
  signInWithEmail: async (email: string, password: string) => {
    const result = await get().signInWithPassword(email, password);
    if (result.error) {
      throw new Error(result.error);
    }
  },

  signUpWithEmail: async (email: string, password: string, nickname?: string, role?: string, inviteCode?: string) => {
    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nickname: nickname || email.split('@')[0],
          },
        },
      });

      if (error) {
        throw error;
      }

      if (!data.session && data.user) {
        throw new Error('User already registered');
      }

      if (data.user && data.session) {
        set({
          session: data.session,
          user: data.user,
          initialized: true,
        });

        try {
          const utmSource = typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('utm_source')
            : null;
          const registrationSource = inviteCode ? 'referral'
            : utmSource === 'chrome-extension' ? 'chrome-extension'
            : 'direct';
          identifyPostHogUser(data.user, {
            created_at: new Date().toISOString(),
            has_invite_code: !!inviteCode,
            invite_code: inviteCode || undefined,
            registration_source: registrationSource,
          });
          posthog.capture('user_signed_up', {
            needs_email_confirmation: false,
            registration_source: registrationSource,
            has_invite_code: !!inviteCode,
            role: role || 'student',
          });
          // Google Ads conversion tracking
          if ((window as any).gtag) {
            (window as any).gtag('event', 'sign_up', {
              method: registrationSource,
            });
          }
          if (utmSource === 'chrome-extension') {
            report(ReportEvent.EXTENSION_USER_REGISTERED, {
              registration_source: 'chrome-extension',
            });
          }
        } catch (posthogError) {
          console.warn('Failed to identify user in PostHog (non-blocking):', posthogError);
        }

        try {
          await saveUTMAttributionOnRegistration(data.user.id, email);
        } catch (utmError) {
          console.warn('Failed to save UTM attribution on registration:', utmError);
        }

        // Reddit Pixel signup conversion (with conversionId for deduplication)
        if (typeof window !== 'undefined' && (window as any).rdt) {
          (window as any).rdt('track', 'SignUp', { conversionId: data.user.id });
        }
        // Reddit Conversions API (server-side, non-blocking)
        fetch('/napi/reddit-capi', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'SignUp',
            conversionId: data.user.id,
            screenWidth: typeof window !== 'undefined' ? window.screen.width : undefined,
            screenHeight: typeof window !== 'undefined' ? window.screen.height : undefined,
          }),
        }).catch(() => {});

        const normalizedSignupRole = String(role || '').toLowerCase();
        const persistedRole = normalizedSignupRole === 'parent'
          ? 'parent_v2'
          : normalizedSignupRole === 'teacher'
            ? 'teacher'
            : 'student';

        await supabase
          .from('profiles')
          .update({
            role: persistedRole,
            onboard_data: {
              profile: {
                role: persistedRole,
              },
            },
            has_password: true,
          })
          .eq('id', data.user.id);

        // Claim referral credit if user signed up via referral link
        let referralClaimResult = { teacherJoined: false, code: null as string | null };
        try {
          referralClaimResult = await claimReferralIfNeeded(data.session.access_token);
        } catch (referralError) {
          console.warn('Failed to claim referral on signup (non-blocking):', referralError);
        }

        if (inviteCode) {
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;

            const response = await fetch('/api/referral-invitation/use', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                invitee_id: data.user.id,
                code: inviteCode,
              }),
            });

            const result = await response.json();
            if (!result.success) {
              console.warn('Invitation code not valid:', result.error);
            }
          } catch (inviteErr) {
            console.error('Error using invitation code:', inviteErr);
          }
        }

        if (persistedRole === 'student' && referralClaimResult.teacherJoined) {
          try {
            const { data: latestProfile, error: latestProfileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', data.user.id)
              .single();

            if (latestProfileError) {
              throw latestProfileError;
            }

            await supabase
              .from('profiles')
              .update({
                onboard_data: mergeClassJoinPromptIntoOnboardData(latestProfile, {
                  status: 'joined',
                  completed_at: new Date().toISOString(),
                  joined_via: 'referral_teacher_code',
                }),
              })
              .eq('id', data.user.id);
          } catch (teacherInviteErr) {
            console.warn('Error persisting teacher referral join state (non-blocking):', teacherInviteErr);
          }
        }

        try {
          await get().getProfile(data.user.id);
        } catch (profileError) {
          console.warn('Failed to load profile after sign-up (non-blocking):', profileError);
        }

        // Send verification email (fire-and-forget for parent, important for teacher)
        if (persistedRole === 'teacher' || persistedRole === 'parent_v2') {
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;
            if (accessToken) {
              fetch('/api/auth/send-verification', {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}` },
              }).catch((err) => console.warn('Failed to send verification email:', err));
            }
          } catch (verifyErr) {
            console.warn('Failed to trigger verification email:', verifyErr);
          }
        }
      }

      set({ isLoading: false, error: null });
    } catch (error) {
      console.error('Error signing up with email:', error);
      const message = error instanceof Error ? error.message : 'Failed to sign up';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  signOut: async (options) => {
    set({ isLoading: true, error: null });

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }

      try {
        posthog.capture('user_signed_out');
        posthog.reset();
      } catch (posthogError) {
        console.warn('Failed to reset PostHog on sign-out (non-blocking):', posthogError);
      }

      localStorage.clear();
      sessionStorage.clear();

      set({
        user: null,
        session: null,
        profile: null,
        userDistrict: null,
        isLoading: false,
        error: null,
      });

      const redirectTo = options?.redirectTo ?? '/';
      const hardReload = options?.hardReload ?? true;

      if (typeof window !== 'undefined') {
        if (hardReload) {
          window.location.assign(redirectTo);
        } else {
          window.history.replaceState({}, '', redirectTo);
        }
      }
    } catch (error) {
      console.error('Error signing out:', error);
      const message = error instanceof Error ? error.message : 'Failed to sign out';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  checkAuth: async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }

      const session = data.session ?? null;
      const user = session?.user ?? null;

      set({
        session,
        user,
        initialized: true,
      });

      if (user && user.email) {
        try {
          identifyPostHogUser(user);
        } catch (posthogError) {
          console.warn('PostHog not available during session restore:', posthogError);
        }

        try {
          await get().getProfile(user.id);
        } catch (profileError) {
          console.warn('Failed to load profile during session restore (non-blocking):', profileError);
        }

        return user;
      }

      set({ profile: null, userDistrict: null });
      return null;
    } catch (error) {
      console.error('Error checking auth:', error);
      set({
        user: null,
        session: null,
        profile: null,
        userDistrict: null,
        initialized: true,
        error: error instanceof Error ? error.message : 'Failed to check auth',
      });
      throw error;
    }
  },

  resetPassword: async (email: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    if (normalizedEmail.endsWith('@fakemail.pengi.ai')) {
      throw new Error('Managed student accounts reset password from the parent account.');
    }

    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      throw error;
    }
  },
}));

export default useAuthStore;
